<template>
  <section class="project-section">
    <header class="section-header">
      <h3 class="section-title">{{ $t('Projects.New project popup.Section_frameworks_title') }}</h3>
      <p v-if="includeDescription" class="section-description">
        {{ $t('Projects.New project popup.Section_frameworks_description') }}
      </p>
    </header>

    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
      <div
        v-for="framework in frameworksWithCatsAndDatasets"
        :key="framework.id"
        class="framework-card group"
        :class="{
          'framework-card--selected': selectedFrameworkRef === framework.id,
        }"
        @click="selectFramework(framework.id)"
        :data-cy="'framework-card-' + framework.id"
      >
        <!-- Selection indicator -->
        <div class="absolute top-3 right-3 z-10">
          <div
            class="selection-indicator"
            :class="{ 'selection-indicator--active': selectedFrameworkRef === framework.id }"
          >
            <svg
              v-if="selectedFrameworkRef === framework.id"
              class="w-4 h-4 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>

        <!-- Chart container -->
        <div class="chart-container">
          <RoseChartD3
            :chartId="'preview-' + (framework.id || 'new')"
            :compassId="null"
            :frameworkId="framework.id"
            :categories="framework.categories"
            :datasets="framework.datasets"
            :options="{
              showIcons: false,
              canHover: false,
              canSelectCategory: false,
              canSaveRatings: false,
              canRate: false,
              clickable: false,
              highlightSelectedCategory: false,
              maxValue: framework.maxValue,
              steps: framework.steps,
              viewMode: framework.viewMode,
              chartWidth: 180,
              chartHeight: 180,
              roundToStep: framework.roundToStep,
            }"
          />
        </div>

        <!-- Framework info -->
        <div class="framework-info">
          <h4 class="framework-title">{{ framework.title }}</h4>
          <div class="framework-meta">
            <span class="framework-stat">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              {{ framework.categories?.length || 0 }} {{ $t('Shared.categories') }}
            </span>
          </div>
        </div>

        <!-- Hidden radio for accessibility -->
        <input
          type="radio"
          :id="`framework-${framework.id}`"
          :value="framework.id"
          v-model="selectedFrameworkRef"
          class="sr-only"
          data-cy="framework-checkbox"
        >
      </div>
    </div>

    <p v-if="validationErrors.frameworkIds || validationErrorMessage" class="validation-error">
      {{ validationErrors.frameworkIds || validationErrorMessage }}
    </p>
  </section>
</template>

<script>
import RoseChartD3 from '@/components/shared/RoseChartD3.vue'
import { generateFrameworkPreviewDatasets } from '@/composables/generateFrameworkPreviewDatasets'

export default {
  name: 'ProjectSectionFrameworks',
  components: {
    RoseChartD3,
  },
  props: {
    allowedFrameworks: {
      type: Array,
      required: true,
    },
    selectedFramework: {
      type: Number,
      required: true,
    },
    includeDescription: {
      type: Boolean,
      default: true,
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['selectedFrameworkUpdated'],
  data () {
    return {
      selectedFrameworkRef: null,
      validationErrorMessage: null,
    }
  },
  watch: {
    allowedFrameworks: {
      handler (newVal) {
        console.log('allowedFrameworks changed', newVal)
        this.selectedFrameworkRef = newVal.length > 0 ? newVal[0].id : null
      },
      immediate: true,
    },
    selectedFramework: {
      handler (newVal) {
        console.log('selectedFramework changed', newVal)
        this.selectedFrameworkRef = newVal
      },
      immediate: true,
    },
    selectedFrameworkRef (newVal) {
      if (this.validate()) {
        this.$emit('selectedFrameworkUpdated', newVal)
      }
    },
  },
  methods: {
    selectFramework (frameworkId) {
      this.selectedFrameworkRef = frameworkId
    },
    validate () {
      const selectedIsValid = this.validateSelectedFramework(this.selectedFrameworkRef)
      return selectedIsValid
    },
    validateSelectedFramework () {
      console.log('validateSelectedFramework', this.allowedFrameworks, this.selectedFrameworkRef)
      if (!this.selectedFrameworkRef) {
        this.validationErrorMessage = this.$t(
          'Organizations.Popups.Validation_error_no_frameworks_selected',
        )
        return false
      }
      this.validationErrorMessage = null
      return true
    },
    getFrameworkLayerSources (frameworkId) {
      return this.$store.getters['framework-layers-sources/list'].filter(
        (layer) => layer.frameworkId === frameworkId,
      )
    },
  },
  computed: {
    frameworksWithCatsAndDatasets () {
      return this.allowedFrameworks.map((framework) => {
        const layers = this.$store.getters.frameworkLayers(framework.id)
        const frameworkCategories = this.$store.getters['categories/list'].filter(
          (cat) => cat.frameworkId === framework.id,
        )
        return {
          ...framework,
          categories: frameworkCategories,
          datasets: generateFrameworkPreviewDatasets(layers, framework, frameworkCategories),
        }
      })
    },
  },
}
</script>

<style scoped>
.project-section {
  @apply py-2;
}

.section-header {
  @apply mb-2;
}

.section-title {
  @apply text-xl font-semibold text-gray-800 mb-2;
}

.section-description {
  @apply text-gray-500 leading-relaxed;
}

.framework-card {
  @apply relative bg-white rounded-xl border-2 border-gray-200 p-5 cursor-pointer;
  @apply transition-all duration-200 ease-out;
  @apply hover:border-gray-300 hover:shadow-lg hover:-translate-y-0.5;
}

.framework-card--selected {
  @apply border-green-500 shadow-md;
  background-color: rgba(16, 185, 129, 0.05);
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.framework-card--selected:hover {
  @apply border-green-500;
}

.selection-indicator {
  @apply w-6 h-6 rounded-full border-2 border-gray-300 bg-white;
  @apply flex items-center justify-center;
  @apply transition-all duration-200;
}

.selection-indicator--active {
  @apply border-green-500 bg-green-500;
}

.chart-container {
  @apply flex items-center justify-center py-4;
  @apply transition-transform duration-200;
}

.framework-card:hover .chart-container {
  @apply scale-105;
}

.framework-info {
  @apply text-center pt-3 border-t border-gray-100;
}

.framework-title {
  @apply font-semibold text-gray-800 mb-2;
}

.framework-meta {
  @apply flex items-center justify-center gap-4 text-sm text-gray-500;
}

.framework-stat {
  @apply flex items-center gap-1.5;
}

.validation-error {
  @apply text-sm text-red-500 mt-4 flex items-center gap-2;
}

.validation-error::before {
  content: '';
  @apply w-1.5 h-1.5 rounded-full bg-red-500;
}
</style>
